/**
 * The interactive ahwa guide.
 *
 * Four tabs:
 *  1. Learn — a scripted mini-table where the visitor actually plays the
 *     opening, a matching end, and a crosswise double, with shake feedback
 *     on wrong taps.
 *  2. The doubles — flip cards with the CORRECT ahwa names (Dabsh = 5·5,
 *     Dargi = 4·4 …), Arabic script and table notes.
 *  3. Numbers & slang — pip names and vocabulary as called at the table.
 *  4. Scoring — the penalty race: first to the target LOSES.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { getTile } from "@domino/rules-engine";
import { DOUBLE_NAMES, DOUBLE_NOTES, PIP_NAMES, PIP_NAMES_AR } from "../lib/slang";
import { sounds } from "../lib/sound";
import { TileFace } from "./tiles";

interface GuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = "learn" | "doubles" | "slang" | "scoring";

// Tile ids in the double-six set.
const T_44 = 22;
const T_46 = 24;
const T_42 = 15;
const T_66 = 27;

function tileIdOfDouble(v: number): number {
  // ids: 0:(0,0) 7:(1,1) 13:(2,2) 18:(3,3) 22:(4,4) 25:(5,5) 27:(6,6)
  return [0, 7, 13, 18, 22, 25, 27][v] ?? 0;
}

export function GuideModal({ isOpen, onClose }: GuideModalProps) {
  const [tab, setTab] = useState<Tab>("learn");
  if (!isOpen) return null;
  return (
    <div className="guide-overlay" onClick={onClose}>
      <div className="guide-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <header className="guide-header">
          <div className="guide-heading">
            <span className="guide-glyph" aria-hidden>
              ۞
            </span>
            <div>
              <h2>دليل دومينو</h2>
              <p>The ahwa guide — play it, don't just read it</p>
            </div>
          </div>
          <button type="button" className="guide-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <nav className="guide-tabs">
          {(
            [
              ["learn", "اتعلم العب", "Learn by playing"],
              ["doubles", "الدبلات", "The doubles"],
              ["slang", "الأرقام والكلام", "Numbers & slang"],
              ["scoring", "الخسران", "Scoring"],
            ] as Array<[Tab, string, string]>
          ).map(([id, ar, en]) => (
            <button
              key={id}
              type="button"
              className={`guide-tab ${tab === id ? "guide-tab-active" : ""}`}
              onClick={() => {
                sounds.ui();
                setTab(id);
              }}
            >
              <span className="guide-tab-ar">{ar}</span>
              <span className="guide-tab-en">{en}</span>
            </button>
          ))}
        </nav>

        <div className="guide-body">
          {tab === "learn" && <LearnTab />}
          {tab === "doubles" && <DoublesTab />}
          {tab === "slang" && <SlangTab />}
          {tab === "scoring" && <ScoringTab />}
        </div>

        <footer className="guide-footer">
          <button type="button" className="action-btn action-primary" onClick={onClose}>
            يلا نلعب · Let's play
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Learn by playing (scripted)
// ---------------------------------------------------------------------------

type LearnPhase = "deal" | "opening" | "match" | "double" | "qafla" | "done";

interface DemoState {
  chain: Array<{ tileId: number; crosswise: boolean; at: "left" | "center" | "right" }>;
  ends: [number, number] | null;
  hand: number[];
  hint: string;
  hintAr: string;
  /** Tile ids the player is expected to play now; empty = nothing to do. */
  expects: number[];
}

const START_DEMO: DemoState = {
  chain: [],
  ends: null,
  hand: [T_44],
  hint: "Round one opens with the highest double. You hold Dargi 4·4 — slam it in the middle.",
  hintAr: "أعلى دبل يفتح — عندك درجي ٤·٤ اطرحها في النص",
  expects: [T_44],
};

const STEP_LABELS: Record<LearnPhase, string> = {
  deal: "١ · الخلاطة — the shuffle",
  opening: "٢ · الافتتاح — the opener",
  match: "٣ · النقطة — matching an end",
  double: "٤ · الدبل عرضة — doubles crosswise",
  qafla: "٥ · القفلة — the block",
  done: "خلاص! — you can play",
};

function LearnTab() {
  const [phase, setPhase] = useState<LearnPhase>("deal");
  const [demo, setDemo] = useState<DemoState>(START_DEMO);
  const [dealt, setDealt] = useState(false);
  const [shakeTile, setShakeTile] = useState<number | null>(null);
  const [counted, setCounted] = useState(false);

  const replay = useCallback(() => {
    sounds.ui();
    setDemo({ ...START_DEMO, hand: [...START_DEMO.hand] });
    setPhase("deal");
    setDealt(false);
    setCounted(false);
  }, []);

  const beginDeal = () => {
    sounds.shuffle(900);
    setTimeout(() => {
      setDealt(true);
      setPhase("opening");
      sounds.dealFlick();
    }, 950);
  };

  const reject = (tileId: number, why: string) => {
    setShakeTile(tileId);
    setTimeout(() => setShakeTile(null), 500);
    sounds.knock();
    setDemo((s) => ({ ...s, hint: why }));
  };

  const onTileTap = (tileId: number) => {
    if (phase !== "opening" && phase !== "match" && phase !== "double") return;
    if (!demo.expects.includes(tileId)) {
      const why =
        phase === "opening"
          ? "The highest double leads — play Dargi 4·4 first."
          : phase === "match"
            ? "Save Gahar Do 4·2 — match an open end with Sheesh Gahar 6·4."
            : "That stone doesn't fit. Drop Dosh 6·6 on the exposed 6.";
      reject(tileId, why);
      return;
    }

    if (phase === "opening") {
      sounds.place(0, 1);
      setDemo((s) => ({
        ...s,
        chain: [{ tileId, crosswise: true, at: "center" }],
        ends: [4, 4],
        hand: [T_46, T_42],
        hint: "The opener exposes both ends — 4 and 4. Tap Sheesh Gahar 6·4 to match the 4.",
        hintAr: "الحجر كشف طرفين — ٤ و ٤. دوس شيش جهار ٦·٤",
        expects: [T_46],
      }));
      setPhase("match");
      return;
    }

    if (phase === "match") {
      sounds.place(0.3, 0.9);
      setDemo((s) => ({
        ...s,
        chain: [...s.chain, { tileId, crosswise: false, at: "right" }],
        ends: [4, 6],
        hand: [T_42, T_66],
        hint: "You drew Dosh 6·6 from el-mashoob! Doubles lie crosswise — drop it on the exposed 6.",
        hintAr: "جبت دش ٦·٦ من المشحوب! الدبل بيتحط عرضة — حطه على الـ ٦",
        expects: [T_66],
      }));
      setPhase("double");
      return;
    }

    if (phase === "double") {
      sounds.slam(0.3);
      setDemo((s) => ({
        ...s,
        chain: [...s.chain, { tileId, crosswise: true, at: "right" }],
        ends: [4, 6],
        hand: [T_42],
        hint: "Crosswise, and the end stays 6. When nobody can match either end — قفلة! Lowest kaf wins the count-up.",
        hintAr: "عرضة، والطرف فاضل ٦. لو محدش يلعب → قفلة والأقل يحجر يكسب",
        expects: [],
      }));
      setPhase("qafla");
    }
  };

  // Animate the block count-up once, after a beat.
  useEffect(() => {
    if (phase !== "qafla" || counted) return;
    const t = setTimeout(() => {
      setCounted(true);
      sounds.counterTick(1);
    }, 900);
    return () => clearTimeout(t);
  }, [phase, counted]);

  const chainCount = useMemo(
    () => demo.chain.reduce((sum, c) => sum + getTile(c.tileId).pipValue, 0),
    [demo.chain],
  );

  return (
    <div className="learn">
      <div className="learn-stage">
        <div className="learn-step-pill">{STEP_LABELS[phase]}</div>

        <div className="learn-felt">
          <div className="learn-chain">
            {demo.chain.length === 0 ? (
              <div className={`learn-mat ${dealt ? "" : "learn-mat-wait"}`}>
                {dealt ? "اضرب الدرجي هنا" : "waiting for the deal…"}
              </div>
            ) : (
              demo.chain.map((c) => (
                <div
                  key={c.tileId}
                  className={`learn-chain-tile ${c.crosswise ? "learn-crosswise" : ""} demo-tile-in`}
                >
                  <TileFace tileId={c.tileId} horizontal={!c.crosswise} size="sm" />
                </div>
              ))
            )}
          </div>

          {demo.ends && (
            <div className="learn-ends">
              <span className="learn-end">
                شمال <b>{demo.ends[0]}</b>
              </span>
              <span className="learn-ends-mid">الطرفين المفتوحين</span>
              <span className="learn-end">
                <b>{demo.ends[1]}</b> يمين
              </span>
            </div>
          )}

          <div className="learn-hand">
            {demo.hand.map((tileId, i) => (
              <div
                key={tileId}
                className={`learn-hand-tile demo-tile-in ${shakeTile === tileId ? "demo-shake" : ""}`}
                style={{ animationDelay: `${(dealt ? i : 0) * 90}ms` }}
                onClick={() => onTileTap(tileId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onTileTap(tileId)}
              >
                <TileFace tileId={tileId} size="md" />
              </div>
            ))}
            {demo.hand.length === 0 && (
              <span className="learn-hand-empty">كفك فاضت — el-dabsha!</span>
            )}
          </div>
        </div>

        <div className="learn-hint">
          <p className="learn-hint-ar">{demo.hintAr}</p>
          <p className="learn-hint-en">{demo.hint}</p>
        </div>

        {phase === "qafla" && counted && (
          <div className="learn-count demo-tile-in">
            <span>
              Count-up: your kaf <b>{chainCount + getTile(T_42).pipValue}</b> · rival <b>11</b> — you
              win, rival takes <b>+11</b> on the glasses.
            </span>
          </div>
        )}

        <div className="learn-controls">
          {phase === "deal" && !dealt && (
            <button type="button" className="action-btn action-gold" onClick={beginDeal}>
              اخلط · Deal me in
            </button>
          )}
          {(phase === "done" || (phase === "qafla" && counted)) && (
            <button type="button" className="action-btn action-gold" onClick={replay}>
              تاني · Replay
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — the doubles flip cards
// ---------------------------------------------------------------------------

function DoublesTab() {
  const [flipped, setFlipped] = useState<number | null>(null);
  const doubles = [6, 5, 4, 3, 2, 1, 0];
  return (
    <div className="doubles">
      <p className="guide-intro">
        Seven doubles, seven names — as shouted across Cairo ahwa tables.{" "}
        <b>Correct names:</b> 5·5 is <b>Dabsh دبش</b>, 4·4 is <b>Dargi درجي</b> — the pair everyone
        mixes up. Tap a card to flip it.
      </p>
      <div className="doubles-grid">
        {doubles.map((v) => {
          const name = DOUBLE_NAMES[v];
          return (
            <button
              key={v}
              type="button"
              className={`double-card ${flipped === v ? "double-card-flipped" : ""}`}
              onClick={() => {
                sounds.place(0, 0.5 + v * 0.07);
                setFlipped((f) => (f === v ? null : v));
              }}
            >
              <span className="double-card-inner">
                <span className="double-card-front">
                  <TileFace tileId={tileIdOfDouble(v)} size="sm" />
                  <span className="double-name">{name?.en}</span>
                  <span className="double-tag">
                    {v}·{v}
                  </span>
                </span>
                <span className="double-card-back">
                  <span className="double-ar">{name?.ar}</span>
                  <span className="double-translit">{name?.en}</span>
                  <span className="double-note">{DOUBLE_NOTES[v]}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — numbers & slang
// ---------------------------------------------------------------------------

function SlangTab() {
  const rows = [6, 5, 4, 3, 2, 1, 0];
  return (
    <div className="slang">
      <div className="slang-numbers">
        <h3 className="guide-subtitle">أسماء الأرقام — pip names</h3>
        <div className="slang-number-grid">
          {rows.map((v) => (
            <div key={v} className="slang-number">
              <span className="slang-number-pip">{v}</span>
              <span className="slang-number-en">{PIP_NAMES[v]}</span>
              <span className="slang-number-ar">{PIP_NAMES_AR[v]}</span>
            </div>
          ))}
        </div>
        <p className="slang-combo-note">
          Big number first: 6·5 is <b>Sheesh Besh شيش بيش</b>, 4·2 is <b>Gahar Do جهار دو</b>.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4 — scoring
// ---------------------------------------------------------------------------

function ScoringTab() {
  return (
    <div className="scoring">
      <p className="guide-intro">
        Egyptian dominoes is a <b>penalty race in reverse</b> — bad rounds push you up a ladder you
        never want to finish.
      </p>
      <div className="scoring-ladder">
        <div className="scoring-glass-rail">
          <span className="scoring-glass demo-fill" />
          <span className="scoring-rail-label">0</span>
          <span className="scoring-rail-label scoring-rail-end">100 = خسرت</span>
        </div>
      </div>
      <ol className="scoring-steps">
        <li>
          <span className="scoring-step-n">1</span>
          <div>
            <b>Win the round, take nothing.</b>
            <p>Go out first (el-dabsha) or hold the fewest pips in a block — zero penalty.</p>
          </div>
        </li>
        <li>
          <span className="scoring-step-n">2</span>
          <div>
            <b>Losers count their own kaf.</b>
            <p>
              Every losing side adds the pips left in ITS own hands. In 2v2 the team adds both
              hands together.
            </p>
          </div>
        </li>
        <li>
          <span className="scoring-step-n">3</span>
          <div>
            <b>First to the target loses the match.</b>
            <p>Default 100. The tea glasses fill as you take penalties — don't let yours top out.</p>
          </div>
        </li>
      </ol>
      <p className="guide-intro">
        Holding <b>Balata 0·0</b> at the count-up costs nothing — the empty stone is your friend.
      </p>
    </div>
  );
}
